/**
 * Movement Mini-App - Database Schema
 *
 * Tables prefixed with `mv_` to namespace from main app.
 * Designed for offline-first with sync capability.
 * Combines fitness tracking (Strava-style) with territory control (zone-style).
 */

import {
  index,
  integer,
  primaryKey,
  real,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { user } from "./auth";

// ──────────────────────────────────────────────────────────────────────────────
// ENUMS & CONSTANTS
// ──────────────────────────────────────────────────────────────────────────────

export const MV_ACTIVITY_TYPES = ["run", "ride", "hike", "walk"] as const;
export type MvActivityType = (typeof MV_ACTIVITY_TYPES)[number];

export const MV_GAME_MODES = [
  "zone", // Closed-loop polygon capture with overlap transfer
  "total_distance", // Seasonal distance contest per hex
] as const;
export type MvGameMode = (typeof MV_GAME_MODES)[number];

export const MV_ZONE_CAPTURE_STATUS = [
  "pending_review",
  "approved",
  "invalid_closure",
  "rejected",
  "auto_rejected",
  "ineligible_activity",
  "invalid_geometry",
] as const;
export type MvZoneCaptureStatus = (typeof MV_ZONE_CAPTURE_STATUS)[number];

export const MV_TOTAL_DISTANCE_CAPTURE_STATUS = [
  "pending_review",
  "approved",
  "rejected",
  "auto_rejected",
  "ineligible_activity",
] as const;
export type MvTotalDistanceCaptureStatus =
  (typeof MV_TOTAL_DISTANCE_CAPTURE_STATUS)[number];

export const MV_UNITS = ["metric", "imperial"] as const;
export type MvUnit = (typeof MV_UNITS)[number];

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITIES (Completed Workouts)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * A completed movement activity (run, ride, hike, walk).
 * Similar to wt_workout pattern with offline sync support.
 */
export const mvActivity = sqliteTable(
  "mv_activity",
  {
    id: text("id").primaryKey(), // UUID

    // Offline-first: client generates ID, syncs later
    clientId: text("client_id"), // For offline sync deduplication

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    activityType: text("activity_type").$type<MvActivityType>().default("run"),
    name: text("name"), // Auto-generated or custom (e.g., "Morning Run")

    // Timing
    startedAt: text("started_at").notNull(),
    completedAt: text("completed_at"),
    durationSeconds: integer("duration_seconds"),
    movingTimeSeconds: integer("moving_time_seconds"), // Excludes pauses
    recordingGapSeconds: integer("recording_gap_seconds"),
    maxRecordingGapSeconds: integer("max_recording_gap_seconds"),
    qualityFlags: text("quality_flags", { mode: "json" }).$type<string[]>(),

    // Core stats
    distanceMeters: real("distance_meters"),
    elevationGainMeters: real("elevation_gain_meters"),
    elevationLossMeters: real("elevation_loss_meters"),

    // Pace/Speed
    avgPaceSecondsPerKm: real("avg_pace_seconds_per_km"),
    avgSpeedMps: real("avg_speed_mps"), // meters per second
    maxSpeedMps: real("max_speed_mps"),

    // Heart rate (if available)
    avgHeartRate: integer("avg_heart_rate"),
    maxHeartRate: integer("max_heart_rate"),

    // Calories (estimated)
    caloriesBurned: integer("calories_burned"),

    // Territory
    hexesClaimed: integer("hexes_claimed").default(0),
    hexesExplored: integer("hexes_explored").default(0), // New hexes visited

    // Weather (logged at start)
    weatherCondition: text("weather_condition"),
    temperatureCelsius: real("temperature_celsius"),

    // Notes
    notes: text("notes"),

    // Privacy - whether this activity is public
    isPublic: integer("is_public", { mode: "boolean" }).default(true),

    // Sync status
    syncedAt: text("synced_at"),
    isDeleted: integer("is_deleted", { mode: "boolean" }).default(false),

    ...timestamps,
  },
  (t) => [
    index("mv_activity_user_idx").on(t.userId),
    index("mv_activity_user_date_idx").on(t.userId, t.startedAt),
    index("mv_activity_client_idx").on(t.clientId),
    uniqueIndex("mv_activity_user_client_unique").on(t.userId, t.clientId),
    index("mv_activity_type_idx").on(t.activityType),
  ],
);

export type MvActivity = typeof mvActivity.$inferSelect;
export type MvActivityInsert = typeof mvActivity.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITY POINTS (GPS Breadcrumbs)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * GPS breadcrumbs for each activity.
 * High volume - should be synced in batches.
 */
export const mvActivityPoint = sqliteTable(
  "mv_activity_point",
  {
    id: text("id").primaryKey(), // UUID
    activityId: text("activity_id")
      .notNull()
      .references(() => mvActivity.id, { onDelete: "cascade" }),

    // GPS coordinates
    sampleId: text("sample_id"),
    segmentId: integer("segment_id"),
    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),
    altitude: real("altitude"), // meters above sea level
    accuracy: real("accuracy"), // GPS accuracy in meters

    // Timestamp of this point
    timestamp: text("timestamp").notNull(),

    // Instantaneous values at this point
    speedMps: real("speed_mps"),
    heartRate: integer("heart_rate"),
    cadence: integer("cadence"), // steps/min for running

    // H3 hexagon at resolution 9 (~174m cells) for territory
    h3Index: text("h3_index"),

    // Order within activity (for efficient querying)
    pointIndex: integer("point_index"),

    ...timestamps,
  },
  (t) => [
    index("mv_activity_point_activity_idx").on(t.activityId),
    index("mv_activity_point_h3_idx").on(t.h3Index),
    index("mv_activity_point_order_idx").on(t.activityId, t.pointIndex),
  ],
);

export type MvActivityPoint = typeof mvActivityPoint.$inferSelect;
export type MvActivityPointInsert = typeof mvActivityPoint.$inferInsert;

/**
 * Full-fidelity owner-only activity points used for territory processing.
 * These points are not privacy-trimmed and should not be shared directly.
 */
export const mvActivityTerritoryPoint = sqliteTable(
  "mv_activity_territory_point",
  {
    id: text("id").primaryKey(),
    activityId: text("activity_id")
      .notNull()
      .references(() => mvActivity.id, { onDelete: "cascade" }),
    sampleId: text("sample_id"),
    segmentId: integer("segment_id"),
    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),
    altitude: real("altitude"),
    accuracy: real("accuracy"),
    speedMps: real("speed_mps"),
    timestamp: text("timestamp").notNull(),
    pointIndex: integer("point_index"),
    ...timestamps,
  },
  (t) => [
    index("mv_activity_territory_point_activity_idx").on(t.activityId),
    index("mv_activity_territory_point_order_idx").on(
      t.activityId,
      t.pointIndex,
    ),
  ],
);

export type MvActivityTerritoryPoint =
  typeof mvActivityTerritoryPoint.$inferSelect;
export type MvActivityTerritoryPointInsert =
  typeof mvActivityTerritoryPoint.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITY SYNC SESSIONS (In-Progress Trail Durability)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Server-side durable checkpoint for in-progress activity tracking.
 * Enables crash-safe resume before final activity completion.
 */
export const mvActivitySyncSession = sqliteTable(
  "mv_activity_sync_session",
  {
    id: text("id").primaryKey(), // UUID

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    // Matches client-generated activity session identifier
    clientId: text("client_id").notNull(),

    activityType: text("activity_type").$type<MvActivityType>().notNull(),
    startedAt: text("started_at").notNull(),

    contextType: text("context_type"),
    contextId: text("context_id"),
    routeId: text("route_id"),

    // ISO timestamp of latest successful point sync
    lastSyncedAt: text("last_synced_at"),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("mv_activity_sync_session_user_client_unique").on(
      t.userId,
      t.clientId,
    ),
    index("mv_activity_sync_session_user_idx").on(t.userId),
    index("mv_activity_sync_session_client_idx").on(t.clientId),
    index("mv_activity_sync_session_updated_idx").on(t.updatedAt),
  ],
);

export type MvActivitySyncSession = typeof mvActivitySyncSession.$inferSelect;
export type MvActivitySyncSessionInsert =
  typeof mvActivitySyncSession.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITY SYNC POINTS (Durable Partial Trail)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Durable point stream for in-progress activities.
 * Deduplicated by (sessionId, pointIndex) to make retries idempotent.
 */
export const mvActivitySyncPoint = sqliteTable(
  "mv_activity_sync_point",
  {
    id: text("id").primaryKey(), // UUID
    sessionId: text("session_id")
      .notNull()
      .references(() => mvActivitySyncSession.id, { onDelete: "cascade" }),

    pointIndex: integer("point_index").notNull(),

    sampleId: text("sample_id"),
    segmentId: integer("segment_id"),
    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),
    altitude: real("altitude"),
    accuracy: real("accuracy"),
    speedMps: real("speed_mps"),
    timestamp: text("timestamp").notNull(),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("mv_activity_sync_point_session_sample_unique").on(
      t.sessionId,
      t.sampleId,
    ),
    uniqueIndex("mv_activity_sync_point_session_point_unique").on(
      t.sessionId,
      t.pointIndex,
    ),
    index("mv_activity_sync_point_session_idx").on(t.sessionId),
    index("mv_activity_sync_point_session_order_idx").on(
      t.sessionId,
      t.pointIndex,
    ),
  ],
);

export type MvActivitySyncPoint = typeof mvActivitySyncPoint.$inferSelect;
export type MvActivitySyncPointInsert = typeof mvActivitySyncPoint.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ROUTES (Saved/Planned Routes)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Saved routes for navigation and re-running.
 */
export const mvRoute = sqliteTable(
  "mv_route",
  {
    id: text("id").primaryKey(), // UUID
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    name: text("name").notNull(),
    description: text("description"),

    // Route stats
    distanceMeters: real("distance_meters"),
    elevationGainMeters: real("elevation_gain_meters"),
    estimatedDurationSeconds: integer("estimated_duration_seconds"),

    // Difficulty rating (1-5)
    difficulty: integer("difficulty"),

    // Activity type this route is for
    activityType: text("activity_type").$type<MvActivityType>().default("run"),

    // Bounding box for quick spatial queries
    minLat: real("min_lat"),
    maxLat: real("max_lat"),
    minLng: real("min_lng"),
    maxLng: real("max_lng"),

    // Source: "drawn", "imported", "recorded"
    source: text("source").default("drawn"),

    // Times used
    timesUsed: integer("times_used").default(0),
    lastUsedAt: text("last_used_at"),

    // Public visibility
    isPublic: integer("is_public", { mode: "boolean" }).default(false),

    ...timestamps,
  },
  (t) => [
    index("mv_route_user_idx").on(t.userId),
    index("mv_route_type_idx").on(t.activityType),
  ],
);

export type MvRoute = typeof mvRoute.$inferSelect;
export type MvRouteInsert = typeof mvRoute.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ROUTE POINTS (GPS Points for Routes)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * GPS points defining a saved route.
 */
export const mvRoutePoint = sqliteTable(
  "mv_route_point",
  {
    id: text("id").primaryKey(), // UUID
    routeId: text("route_id")
      .notNull()
      .references(() => mvRoute.id, { onDelete: "cascade" }),

    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),
    altitude: real("altitude"),

    // Order within route
    pointIndex: integer("point_index").notNull(),

    // Optional: turn instruction for navigation
    instruction: text("instruction"),

    ...timestamps,
  },
  (t) => [
    index("mv_route_point_route_idx").on(t.routeId),
    index("mv_route_point_order_idx").on(t.routeId, t.pointIndex),
  ],
);

export type MvRoutePoint = typeof mvRoutePoint.$inferSelect;
export type MvRoutePointInsert = typeof mvRoutePoint.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// TERRITORY (Hexagon Ownership)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Territory ownership per H3 hexagon.
 * Each game mode has different ownership rules.
 */
export const mvTerritory = sqliteTable(
  "mv_territory",
  {
    // H3 hex index at resolution 9
    h3Index: text("h3_index").notNull(),

    // Game mode this territory belongs to
    gameMode: text("game_mode").$type<MvGameMode>().notNull(),

    // Activity-specific territory pool (run, ride, hike, walk)
    activityType: text("activity_type").$type<MvActivityType>().notNull(),

    // Current owner
    ownerId: text("owner_id").references(() => user.id, {
      onDelete: "set null",
    }),

    // Center coordinates for spatial queries
    centerLat: real("center_lat"),
    centerLng: real("center_lng"),

    // Last activity touching this territory
    lastClaimedAt: text("last_claimed_at"),

    // Activity that claimed this territory
    claimActivityId: text("claim_activity_id"),

    // Stats
    totalClaims: integer("total_claims").default(0),

    ...timestamps,
  },
  (t) => [
    primaryKey({ columns: [t.h3Index, t.gameMode, t.activityType] }),
    index("mv_territory_owner_idx").on(t.ownerId),
    index("mv_territory_mode_idx").on(t.gameMode, t.activityType),
    index("mv_territory_last_claimed_idx").on(
      t.gameMode,
      t.activityType,
      t.lastClaimedAt,
    ),
    index("mv_territory_spatial_idx").on(t.centerLat, t.centerLng),
  ],
);

export type MvTerritory = typeof mvTerritory.$inferSelect;
export type MvTerritoryInsert = typeof mvTerritory.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// TERRITORY CLAIMS (History for Leaderboards)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * History of territory claims for leaderboards and stats.
 */
export const mvTerritoryClaim = sqliteTable(
  "mv_territory_claim",
  {
    id: text("id").primaryKey(), // UUID

    h3Index: text("h3_index").notNull(),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    gameMode: text("game_mode").$type<MvGameMode>().notNull(),
    activityType: text("activity_type").$type<MvActivityType>().notNull(),

    // For Total Distance: distance covered in this hex for this activity contribution (meters)
    distanceMeters: real("distance_meters"),

    // Reference to activity that made the claim
    activityId: text("activity_id").references(() => mvActivity.id, {
      onDelete: "set null",
    }),

    claimedAt: text("claimed_at").notNull(),

    // Was this claim successful (did it actually claim the hex)?
    wasSuccessful: integer("was_successful", { mode: "boolean" }).default(true),

    ...timestamps,
  },
  (t) => [
    index("mv_claim_user_idx").on(t.userId),
    index("mv_claim_hex_idx").on(t.h3Index),
    index("mv_claim_mode_idx").on(t.gameMode, t.activityType),
    index("mv_claim_date_idx").on(t.claimedAt),
    index("mv_claim_mode_hex_date_idx").on(
      t.gameMode,
      t.activityType,
      t.h3Index,
      t.claimedAt,
    ),
  ],
);

export type MvTerritoryClaim = typeof mvTerritoryClaim.$inferSelect;
export type MvTerritoryClaimInsert = typeof mvTerritoryClaim.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ZONE CAPTURE ATTEMPTS
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Capture attempts for Zone mode.
 * Includes review state, suspicious flags, and trim metadata.
 */
export const mvZoneCapture = sqliteTable(
  "mv_zone_capture",
  {
    id: text("id").primaryKey(),
    activityId: text("activity_id")
      .notNull()
      .references(() => mvActivity.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    activityType: text("activity_type").$type<MvActivityType>().notNull(),
    startedAt: text("started_at").notNull(),
    completedAt: text("completed_at"),
    attemptStatus: text("attempt_status")
      .$type<MvZoneCaptureStatus>()
      .notNull(),
    closureDistanceMeters: real("closure_distance_meters"),
    captureDistanceMeters: real("capture_distance_meters"),
    captureAreaM2: real("capture_area_m2"),
    geometryGeojson: text("geometry_geojson", { mode: "json" }).$type<Record<
      string,
      unknown
    > | null>(),
    suspiciousFlags: text("suspicious_flags", { mode: "json" }).$type<
      string[] | null
    >(),
    suspiciousScore: real("suspicious_score"),
    requiresReview: integer("requires_review", { mode: "boolean" }).default(
      false,
    ),
    reviewNotes: text("review_notes"),
    reviewedBy: text("reviewed_by").references(() => user.id, {
      onDelete: "set null",
    }),
    reviewedAt: text("reviewed_at"),
    autoRejectAt: text("auto_reject_at"),
    trimStartIndex: integer("trim_start_index"),
    trimEndIndex: integer("trim_end_index"),
    trimmedFromCaptureId: text("trimmed_from_capture_id"),
    trimmedAt: text("trimmed_at"),
    isCaptureApplied: integer("is_capture_applied", {
      mode: "boolean",
    }).default(false),
    newClaimsCount: integer("new_claims_count").default(0),
    reclaimsCount: integer("reclaims_count").default(0),
    appliedClaimsCount: integer("applied_claims_count").default(0),
    h3Resolution: integer("h3_resolution").default(12),
    ...timestamps,
  },
  (t) => [
    index("mv_zone_capture_activity_idx").on(t.activityId),
    index("mv_zone_capture_user_idx").on(t.userId),
    index("mv_zone_capture_status_idx").on(t.attemptStatus),
    index("mv_zone_capture_review_idx").on(t.requiresReview, t.attemptStatus),
    index("mv_zone_capture_auto_reject_idx").on(
      t.autoRejectAt,
      t.attemptStatus,
    ),
    index("mv_zone_capture_started_idx").on(t.startedAt),
  ],
);

export type MvZoneCapture = typeof mvZoneCapture.$inferSelect;
export type MvZoneCaptureInsert = typeof mvZoneCapture.$inferInsert;

/**
 * Capture attempts for Total Distance mode.
 * Mirrors Zone review state but applies seasonal hex distance claims.
 */
export const mvTotalDistanceCapture = sqliteTable(
  "mv_total_distance_capture",
  {
    id: text("id").primaryKey(),
    activityId: text("activity_id")
      .notNull()
      .references(() => mvActivity.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    activityType: text("activity_type").$type<MvActivityType>().notNull(),
    startedAt: text("started_at").notNull(),
    completedAt: text("completed_at"),
    attemptStatus: text("attempt_status")
      .$type<MvTotalDistanceCaptureStatus>()
      .notNull(),
    captureDistanceMeters: real("capture_distance_meters"),
    affectedHexCount: integer("affected_hex_count").default(0),
    seasonNumber: integer("season_number").notNull(),
    seasonStartIso: text("season_start_iso").notNull(),
    seasonEndIso: text("season_end_iso").notNull(),
    suspiciousFlags: text("suspicious_flags", { mode: "json" }).$type<
      string[] | null
    >(),
    suspiciousScore: real("suspicious_score"),
    requiresReview: integer("requires_review", { mode: "boolean" }).default(
      false,
    ),
    reviewNotes: text("review_notes"),
    reviewedBy: text("reviewed_by").references(() => user.id, {
      onDelete: "set null",
    }),
    reviewedAt: text("reviewed_at"),
    autoRejectAt: text("auto_reject_at"),
    isCaptureApplied: integer("is_capture_applied", {
      mode: "boolean",
    }).default(false),
    newClaimsCount: integer("new_claims_count").default(0),
    reclaimsCount: integer("reclaims_count").default(0),
    appliedClaimsCount: integer("applied_claims_count").default(0),
    ...timestamps,
  },
  (t) => [
    index("mv_total_distance_capture_activity_idx").on(t.activityId),
    index("mv_total_distance_capture_user_idx").on(t.userId),
    index("mv_total_distance_capture_status_idx").on(t.attemptStatus),
    index("mv_total_distance_capture_review_idx").on(
      t.requiresReview,
      t.attemptStatus,
    ),
    index("mv_total_distance_capture_auto_reject_idx").on(
      t.autoRejectAt,
      t.attemptStatus,
    ),
    index("mv_total_distance_capture_started_idx").on(t.startedAt),
  ],
);

export type MvTotalDistanceCapture = typeof mvTotalDistanceCapture.$inferSelect;
export type MvTotalDistanceCaptureInsert =
  typeof mvTotalDistanceCapture.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ZONE TERRITORY PARCELS
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Canonical Zone territory parcels as polygon/multipolygon geometry.
 */
export const mvZoneTerritory = sqliteTable(
  "mv_zone_territory",
  {
    id: text("id").primaryKey(),
    sourceCaptureId: text("source_capture_id").references(
      () => mvZoneCapture.id,
      {
        onDelete: "set null",
      },
    ),
    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    activityType: text("activity_type").$type<MvActivityType>().notNull(),
    geometryGeojson: text("geometry_geojson", { mode: "json" })
      .$type<Record<string, unknown>>()
      .notNull(),
    bboxMinLat: real("bbox_min_lat").notNull(),
    bboxMaxLat: real("bbox_max_lat").notNull(),
    bboxMinLng: real("bbox_min_lng").notNull(),
    bboxMaxLng: real("bbox_max_lng").notNull(),
    areaM2: real("area_m2").notNull(),
    centroidLat: real("centroid_lat"),
    centroidLng: real("centroid_lng"),
    ...timestamps,
  },
  (t) => [
    index("mv_zone_territory_capture_idx").on(t.sourceCaptureId),
    index("mv_zone_territory_activity_idx").on(t.activityType),
    index("mv_zone_territory_bbox_idx").on(
      t.activityType,
      t.bboxMinLat,
      t.bboxMaxLat,
      t.bboxMinLng,
      t.bboxMaxLng,
    ),
    index("mv_zone_territory_centroid_idx").on(t.centroidLat, t.centroidLng),
    index("mv_zone_territory_area_idx").on(t.areaM2),
  ],
);

export type MvZoneTerritory = typeof mvZoneTerritory.$inferSelect;
export type MvZoneTerritoryInsert = typeof mvZoneTerritory.$inferInsert;

export const mvZoneTerritoryOwner = sqliteTable(
  "mv_zone_territory_owner",
  {
    territoryId: text("territory_id")
      .notNull()
      .references(() => mvZoneTerritory.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    addedByCaptureId: text("added_by_capture_id").references(
      () => mvZoneCapture.id,
      {
        onDelete: "set null",
      },
    ),
    addedAt: text("added_at").notNull(),
    ...timestamps,
  },
  (t) => [
    primaryKey({ columns: [t.territoryId, t.userId] }),
    index("mv_zone_territory_owner_user_idx").on(t.userId),
    index("mv_zone_territory_owner_capture_idx").on(t.addedByCaptureId),
  ],
);

export type MvZoneTerritoryOwner = typeof mvZoneTerritoryOwner.$inferSelect;
export type MvZoneTerritoryOwnerInsert =
  typeof mvZoneTerritoryOwner.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ZONE CLAIM HISTORY
// ──────────────────────────────────────────────────────────────────────────────

export const mvZoneClaim = sqliteTable(
  "mv_zone_claim",
  {
    id: text("id").primaryKey(),
    captureId: text("capture_id")
      .notNull()
      .references(() => mvZoneCapture.id, { onDelete: "cascade" }),
    territoryId: text("territory_id").references(() => mvZoneTerritory.id, {
      onDelete: "set null",
    }),
    claimantUserId: text("claimant_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    claimType: text("claim_type").notNull(),
    previousOwnerIds: text("previous_owner_ids", { mode: "json" }).$type<
      string[] | null
    >(),
    overlapWithTerritoryId: text("overlap_with_territory_id"),
    geometryGeojson: text("geometry_geojson", { mode: "json" }).$type<Record<
      string,
      unknown
    > | null>(),
    areaM2: real("area_m2").notNull(),
    claimedAt: text("claimed_at").notNull(),
    ...timestamps,
  },
  (t) => [
    index("mv_zone_claim_capture_idx").on(t.captureId),
    index("mv_zone_claim_territory_idx").on(t.territoryId),
    index("mv_zone_claim_user_idx").on(t.claimantUserId),
    index("mv_zone_claim_date_idx").on(t.claimedAt),
    index("mv_zone_claim_type_idx").on(t.claimType),
  ],
);

export type MvZoneClaim = typeof mvZoneClaim.$inferSelect;
export type MvZoneClaimInsert = typeof mvZoneClaim.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ZONE H3 ACCELERATION INDEXES
// ──────────────────────────────────────────────────────────────────────────────

export const mvZoneTerritoryH3 = sqliteTable(
  "mv_zone_territory_h3",
  {
    territoryId: text("territory_id")
      .notNull()
      .references(() => mvZoneTerritory.id, { onDelete: "cascade" }),
    h3Index: text("h3_index").notNull(),
    resolution: integer("resolution").notNull().default(12),
    ...timestamps,
  },
  (t) => [
    primaryKey({ columns: [t.territoryId, t.h3Index, t.resolution] }),
    index("mv_zone_territory_h3_lookup_idx").on(t.h3Index, t.resolution),
  ],
);

export type MvZoneTerritoryH3 = typeof mvZoneTerritoryH3.$inferSelect;
export type MvZoneTerritoryH3Insert = typeof mvZoneTerritoryH3.$inferInsert;

export const mvZoneCaptureH3 = sqliteTable(
  "mv_zone_capture_h3",
  {
    captureId: text("capture_id")
      .notNull()
      .references(() => mvZoneCapture.id, { onDelete: "cascade" }),
    h3Index: text("h3_index").notNull(),
    resolution: integer("resolution").notNull().default(12),
    ...timestamps,
  },
  (t) => [
    primaryKey({ columns: [t.captureId, t.h3Index, t.resolution] }),
    index("mv_zone_capture_h3_lookup_idx").on(t.h3Index, t.resolution),
  ],
);

export type MvZoneCaptureH3 = typeof mvZoneCaptureH3.$inferSelect;
export type MvZoneCaptureH3Insert = typeof mvZoneCaptureH3.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ZONE CO-RUN DEDUP LINKS
// ──────────────────────────────────────────────────────────────────────────────

export const mvZoneCorunLink = sqliteTable(
  "mv_zone_corun_link",
  {
    id: text("id").primaryKey(),
    primaryCaptureId: text("primary_capture_id")
      .notNull()
      .references(() => mvZoneCapture.id, { onDelete: "cascade" }),
    secondaryCaptureId: text("secondary_capture_id")
      .notNull()
      .references(() => mvZoneCapture.id, { onDelete: "cascade" }),
    pairHash: text("pair_hash").notNull(),
    overlapRatio: real("overlap_ratio").notNull(),
    overlapAreaM2: real("overlap_area_m2").notNull(),
    overlapGeometryGeojson: text("overlap_geometry_geojson", {
      mode: "json",
    }).$type<Record<string, unknown> | null>(),
    appliedAt: text("applied_at").notNull(),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("mv_zone_corun_pair_hash_unique").on(t.pairHash),
    index("mv_zone_corun_primary_idx").on(t.primaryCaptureId),
    index("mv_zone_corun_secondary_idx").on(t.secondaryCaptureId),
    index("mv_zone_corun_applied_idx").on(t.appliedAt),
  ],
);

export type MvZoneCorunLink = typeof mvZoneCorunLink.$inferSelect;
export type MvZoneCorunLinkInsert = typeof mvZoneCorunLink.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// USER SETTINGS
// ──────────────────────────────────────────────────────────────────────────────

/**
 * User preferences for the movement app.
 */
export const mvUserSettings = sqliteTable("mv_user_settings", {
  userId: text("user_id")
    .primaryKey()
    .references(() => user.id, { onDelete: "cascade" }),

  // Units
  units: text("units").$type<MvUnit>().default("metric"),

  // Default activity type
  defaultActivityType: text("default_activity_type")
    .$type<MvActivityType>()
    .default("run"),

  // Audio cues
  audioEnabled: integer("audio_enabled", { mode: "boolean" }).default(true),
  announceDistance: integer("announce_distance", { mode: "boolean" }).default(
    true,
  ),
  announcePace: integer("announce_pace", { mode: "boolean" }).default(true),
  distanceInterval: integer("distance_interval").default(1000), // meters

  // Auto-pause
  autoPauseEnabled: integer("auto_pause_enabled", { mode: "boolean" }).default(
    true,
  ),
  autoPauseSpeedThreshold: real("auto_pause_speed_threshold").default(0.5), // m/s

  // Map preferences
  mapStyle: text("map_style").default("standard"),
  showTerritoryOverlay: integer("show_territory_overlay", {
    mode: "boolean",
  }).default(true),

  ...timestamps,
});

export type MvUserSettings = typeof mvUserSettings.$inferSelect;
export type MvUserSettingsInsert = typeof mvUserSettings.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// PRIVACY ZONES
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Privacy zones to hide sensitive locations (home, work).
 */
export const mvPrivacyZone = sqliteTable(
  "mv_privacy_zone",
  {
    id: text("id").primaryKey(), // UUID
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    name: text("name").notNull(), // "Home", "Work", etc.

    // Center point
    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),

    // Radius in meters (100m - 1000m)
    radiusMeters: integer("radius_meters").default(200),

    ...timestamps,
  },
  (t) => [index("mv_privacy_zone_user_idx").on(t.userId)],
);

export type MvPrivacyZone = typeof mvPrivacyZone.$inferSelect;
export type MvPrivacyZoneInsert = typeof mvPrivacyZone.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// STREAKS (Activity Consistency Tracking)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Track user activity streaks.
 */
export const mvStreak = sqliteTable("mv_streak", {
  userId: text("user_id")
    .primaryKey()
    .references(() => user.id, { onDelete: "cascade" }),

  currentStreak: integer("current_streak").default(0),
  longestStreak: integer("longest_streak").default(0),
  lastActivityDate: text("last_activity_date"),

  // Weekly goal tracking
  weeklyGoalMinutes: integer("weekly_goal_minutes").default(150),
  weeklyMinutesCompleted: integer("weekly_minutes_completed").default(0),
  weekStartDate: text("week_start_date"),

  ...timestamps,
});

export type MvStreak = typeof mvStreak.$inferSelect;
export type MvStreakInsert = typeof mvStreak.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// PERSONAL RECORDS
// ──────────────────────────────────────────────────────────────────────────────

export const MV_RECORD_TYPES = [
  "fastest_1k",
  "fastest_5k",
  "fastest_10k",
  "fastest_half_marathon",
  "fastest_marathon",
  "longest_distance",
  "highest_elevation",
  "most_hexes_claimed",
] as const;
export type MvRecordType = (typeof MV_RECORD_TYPES)[number];

/**
 * Personal records for achievements.
 */
export const mvPersonalRecord = sqliteTable(
  "mv_personal_record",
  {
    id: text("id").primaryKey(), // UUID
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    recordType: text("record_type").$type<MvRecordType>().notNull(),
    value: real("value").notNull(), // Time in seconds or distance in meters

    activityId: text("activity_id").references(() => mvActivity.id, {
      onDelete: "set null",
    }),
    achievedAt: text("achieved_at").notNull(),

    ...timestamps,
  },
  (t) => [
    index("mv_pr_user_idx").on(t.userId),
    uniqueIndex("mv_pr_user_type_idx").on(t.userId, t.recordType),
  ],
);

export type MvPersonalRecord = typeof mvPersonalRecord.$inferSelect;
export type MvPersonalRecordInsert = typeof mvPersonalRecord.$inferInsert;
